import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import {
	ANALYTICS_EVENT_TYPE,
	ANALYTICS_SOURCE,
	ANALYTICS_TYPE,
	ANALYTICS_UI_ACTION,
	ANALYTICS_UI_SUBJECT,
	ANALYTICS_UI_SUBJECT_ID,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';
import type { Snippet } from '../../types/snippet.ts';

/**
 * Shared payload helpers for snippet analytics.
 *
 * These are framework-agnostic so they can be used both by the React
 * `useSnippetAnalytics` hook and by non-React editor-extension code (e.g. the
 * quick-insert action), guaranteeing identical event shapes across surfaces.
 */

export type SnippetUIEventData = {
	actionSubject: string;
	action: string;
	actionSubjectId: string;
	attributes?: Record<string, unknown>;
};

/**
 * Merges an optional origin into attributes. Per-call attributes with the same
 * key take precedence over the supplied origin.
 */
export const mergeOriginIntoAttributes = (
	origin: AnalyticsOrigin | undefined,
	attributes?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
	if (!origin) {
		return attributes;
	}
	return { origin, ...attributes };
};

/**
 * Builds the `createAnalyticsEvent` argument for a snippets UI event, injecting
 * the shared source, type and (optionally) origin.
 */
export const buildSnippetUIEvent = (
	data: SnippetUIEventData,
	origin?: AnalyticsOrigin,
): {
	type: ANALYTICS_EVENT_TYPE;
	data: {
		attributes: Record<string, unknown> | undefined;
		actionSubject: string;
		action: string;
		actionSubjectId: string;
		source: string;
		type: ANALYTICS_TYPE;
	};
} => ({
	type: ANALYTICS_EVENT_TYPE.SEND_UI_EVENT,
	data: {
		source: ANALYTICS_SOURCE,
		type: ANALYTICS_TYPE.UI,
		...data,
		attributes: mergeOriginIntoAttributes(origin, data.attributes),
	},
});

/**
 * Fires a snippets UI event using a raw `createAnalyticsEvent` factory.
 *
 * Intended for non-React contexts (editor-extension code) where the
 * `useSnippetAnalytics` hook is unavailable. No-op when `createAnalyticsEvent`
 * is not provided.
 */
export const fireSnippetUIEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent | null | undefined,
	data: SnippetUIEventData,
	origin?: AnalyticsOrigin,
): void => {
	if (!createAnalyticsEvent) {
		return;
	}
	createAnalyticsEvent(buildSnippetUIEvent(data, origin)).fire();
};

/**
 * Builds the shared `insertSnippet` UI event data for a snippet inserted via
 * quick-insert. Single source of truth for the event shape so the imperative
 * (`createSnippetQuickInsertItem`) and extension-provider (`snippetExtensionManifest`)
 * paths stay in sync.
 *
 * `templateType` is optional on `Snippet`; it is only included when defined so
 * the serialised payload omits the key entirely rather than emitting an
 * explicit `undefined`/`null`.
 */
export const buildInsertSnippetEventData = (snippet: Snippet): SnippetUIEventData => ({
	actionSubject: ANALYTICS_UI_SUBJECT.BUTTON,
	action: ANALYTICS_UI_ACTION.CLICKED,
	actionSubjectId: ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET,
	attributes: {
		scope: snippet.scope,
		snippetId: snippet.snippetId,
		isShared: snippet.isShared,
		...(snippet.templateType !== undefined && { templateType: snippet.templateType }),
	},
});
